import { formatTime } from "@kedaat/engine";
import { formatCountdown, type ZmanRow } from "@/lib/zmanim";

/** Falls back to one hour when nothing came before, so the bar still means something. */
const DEFAULT_SPAN = 60 * 60 * 1000;

/** The next zman today, shown large, with a countdown and a bar filling up towards it. */
export function NextZmanCard({
  rows,
  now,
  seconds,
}: {
  rows: ZmanRow[];
  now: number | null;
  seconds: boolean;
}) {
  if (now === null) return null;
  const next = rows.find((r) => r.ms !== null && r.ms > now);
  if (!next || next.ms === null) return null;

  const previous = [...rows].reverse().find((r) => r.ms !== null && r.ms <= now)?.ms;
  const from = previous ?? next.ms - DEFAULT_SPAN;
  const progress = Math.min(100, Math.max(0, ((now - from) / (next.ms - from)) * 100));

  return (
    <section
      aria-label="הזמן הבא"
      aria-live="polite"
      className="relative overflow-hidden rounded-2xl px-5 py-4 text-paper shadow-lg"
      style={{
        background:
          "linear-gradient(135deg, color-mix(in oklab, var(--color-accent) 88%, var(--color-ink)), var(--color-accent))",
      }}
    >
      <p className="text-sm font-medium opacity-90">הזמן הבא</p>
      <div className="mt-1 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="font-serif text-2xl font-bold leading-tight">{next.zman.label.he}</p>
          <p className="text-sm opacity-90">{next.opinion.label.he}</p>
        </div>
        <div className="text-end">
          <p className="text-5xl font-bold leading-none tabular-nums">
            {formatTime(next.ms, next.opinion.rounding, { seconds })}
          </p>
          <p className="mt-1 text-sm font-medium opacity-90">{formatCountdown(next.ms - now)}</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-paper/25" aria-hidden="true">
        <div
          className="h-full rounded-full bg-paper/85 transition-[width] duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </section>
  );
}
